import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { ArrowLeft, Calendar, Briefcase } from "lucide-react";
import { getAnnouncement, getAnnouncements } from "@/api/announcements";
import { getServices } from "@/api/services";
import { SEO } from "@/components/site/SEO";

export function AnnouncementDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { lang, t } = useLang();
    const [a, setA] = useState<any>(null);
    const [allAnnouncements, setAllAnnouncements] = useState<any[]>([]);
    const [services, setServices] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        Promise.all([
            getAnnouncement(id),
            getAnnouncements(true),
            getServices(true)
        ])
            .then(([annData, annsData, servicesData]) => {
                setA(annData);
                setAllAnnouncements(annsData || []);
                setServices(servicesData.slice(0, 5));
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to load data:', err);
                setLoading(false);
            });
    }, [id]);

    if (loading) return <div className="container mx-auto px-4 py-16 min-h-[50vh]"></div>;
    if (!a) return <div className="container mx-auto px-4 py-16 text-center">Duyuru bulunamadı. <Link to="/announcements" className="text-primary underline">Geri</Link></div>;

    const title = lang === "tr" ? a.title_tr : a.title_en;
    const content = lang === "tr" ? a.content_tr : a.content_en;
    const pubDate = a.created_at ? new Date(a.created_at) : new Date();

    const seoDescription = content
        ? content.replace(/<[^>]*>/g, "").substring(0, 160) + "..."
        : undefined;

    return (
        <div className="container mx-auto px-4 py-12">
            <SEO
                title={title}
                description={seoDescription}
                image={a.image_url}
                url={`/announcements/${id}`}
                type="article"
            />
            <div className="flex flex-col-reverse lg:flex-row gap-10">
                {/* Sidebar */}
                <aside className="w-full lg:w-1/3 xl:w-1/4 flex-shrink-0">
                    <div className="sticky top-28 space-y-6">
                        {/* Announcements */}
                        <div className="bg-white shadow-sm border p-5">
                            <h3 className="font-bold text-lg text-primary mb-4 pb-2 border-b">
                                {lang === "tr" ? "Duyurular" : "Announcements"}
                            </h3>
                            <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                                {allAnnouncements.map((item) => {
                                    const itemTitle = lang === "tr" ? item.title_tr : item.title_en;
                                    const isActive = item.id === id;
                                    const date = new Date(item.created_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                    });
                                    return (
                                        <li key={item.id}>
                                            <Link
                                                to={`/announcements/${item.id}`}
                                                className={`block px-3 py-2 rounded-lg transition-colors ${isActive
                                                        ? "bg-primary text-white shadow-sm"
                                                        : "hover:bg-primary/5"
                                                    }`}
                                            >
                                                <h4 className={`text-sm mb-1 ${isActive ? "font-semibold" : "font-medium text-foreground"}`}>
                                                    {itemTitle}
                                                </h4>
                                                <div className={`flex items-center gap-1 text-xs ${isActive ? "text-white/80" : "text-muted-foreground"}`}>
                                                    <Calendar size={12} />
                                                    <span>{date}</span>
                                                </div>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        {/* Services */}
                        {services.length > 0 && (
                            <div className="bg-white shadow-sm border p-5">
                                <h3 className="font-bold text-lg text-primary mb-4 pb-2 border-b">
                                    {lang === "tr" ? "Hizmetlerimiz" : "Our Services"}
                                </h3>
                                <ul className="space-y-2">
                                    {services.map((item) => {
                                        const itemTitle = lang === "tr" ? item.title_tr : item.title_en;
                                        return (
                                            <li key={item.id}>
                                                <Link
                                                    to={`/services/${item.slug}`}
                                                    className="block px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors group text-sm text-foreground group-hover:text-primary"
                                                >
                                                    {itemTitle}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <Link
                                    to="/services"
                                    className="block mt-4 pt-3 border-t text-center text-sm text-primary font-medium hover:text-primary-dark transition-colors"
                                >
                                    {lang === "tr" ? "Tüm Hizmetler →" : "All Services →"}
                                </Link>
                            </div>
                        )}
                    </div>
                </aside>

                {/* Content */}
                <article className="w-full lg:w-2/3 xl:w-3/4">
                    <Link to="/announcements" className="inline-flex lg:hidden items-center gap-2 text-primary mb-6 hover:opacity-80 transition-opacity">
                        <ArrowLeft size={16} /> {lang === "tr" ? "Tüm Duyurular" : "All Announcements"}
                    </Link>

                    {a.image_url && (
                        <div className="w-full aspect-[21/9] bg-muted overflow-hidden mb-6 shadow-sm">
                            <img src={a.image_url} alt={title || "Duyuru"} className="w-full h-full object-cover" />
                        </div>
                    )}

                    <div className="bg-white shadow-sm border p-6">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                            <Calendar size={16} />{pubDate.toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US")}
                        </div>

                        <h1 className="text-4xl font-bold text-primary mb-6">{title}</h1>

                        <div
                            className="prose max-w-none text-[11pt] [&_p]:!text-[11pt] [&_li]:!text-[11pt] [&_span]:!text-[11pt] [&_div]:!text-[11pt] text-foreground leading-relaxed prose-p:mb-4 prose-headings:text-primary prose-a:text-blue-600 hover:prose-a:text-blue-800"
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    </div>
                </article>
            </div>
        </div>
    );
}
